/**
 * ① Accueil et lecture - le panneau, plus l'ecran.
 *
 * Doit repondre en trois secondes a : qui me demande quoi, et pourquoi ?
 * L'emetteur est donc l'element le plus visible - le destinataire ne
 * connait pas Sign365, il connait notre client.
 *
 * Depuis B-16 (19/09/2026) la visionneuse n'est plus ici : elle vit dans
 * l'ecran unique, a cote de ce panneau, et y reste jusqu'a la signature.
 *
 * B-18 : BLOCAGE DUR. « Continuer » reste inactif tant que le document n'a
 * pas ete mis sous les yeux du signataire. Deux precautions vont avec,
 * sans lesquelles le blocage devient une impasse :
 *
 *   - le compteur et le bouton « aller a la fin du document ». Un bouton
 *     desactive sans raison visible se lit comme une panne, et sur un
 *     contrat de quarante pages, atteindre le bas au doigt est long ;
 *   - le repli par declaration quand la visionneuse echoue. Sans lui, un
 *     signataire dont le navigateur n'affiche pas le PDF ne pourrait plus
 *     signer du tout - et c'est notre client qu'il appellerait.
 *
 * Franck, 21/10/2026 : « defilement des pages jusqu'a la derniere page » -
 * CHAQUE page doit avoir ete vue (B-19 se contentait de la derniere). Le
 * serveur verifie la meme chose, parce qu'un controle qui ne vit que dans
 * le site se contourne en appelant l'API.
 */
import { useState, type CSSProperties } from "react";

import { ApiError, postDocumentReviewed, type SignerView } from "../api";
import type { t } from "../i18n";
import { ErrorPanel } from "../components/ErrorPanel";

interface Props {
  token: string;
  view: SignerView;
  L: ReturnType<typeof t>;
  /** Pages dont le rendu a abouti, donc reellement affichees. */
  pagesVues: number[];
  pageCount: number;
  viewerFailed: boolean;
  documentPret: boolean;
  onAllerALaPage: (page: number) => void;
  onDownload: () => void;
  onDone: () => void;
  onDecline: () => void;
}

export function Review({
  token, view, L, pagesVues, pageCount, viewerFailed, documentPret,
  onAllerALaPage, onDownload, onDone, onDecline,
}: Props) {
  const [declare, setDeclare] = useState(false);
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const level = view.signature_level ? L.levels[view.signature_level] : null;

  const vues = pagesVues.filter((n) => n >= 1 && n <= pageCount).length;
  const premiereNonVue = Array.from({ length: pageCount }, (_, i) => i + 1).find((n) => !pagesVues.includes(n));
  const finAtteinte = pageCount > 0 && premiereNonVue === undefined;

  const pretAContinuer = viewerFailed ? declare : finAtteinte;

  const submit = async () => {
    if (viewerFailed && !declare) {
      setTouched(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await postDocumentReviewed(
        token,
        // Les pages REELLEMENT affichees, jamais arrondies au total : c'est
        // ce nombre que le dossier de preuve imprime, et un « 40 sur 40 »
        // pour quelqu'un qui a tire l'ascenseur serait une affirmation que
        // nous ne pourrions pas soutenir.
        viewerFailed ? [] : pagesVues,
        viewerFailed ? "declaration" : "defilement",
      );
      onDone();
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError("server", 0, null);
      // Lecture deja enregistree (double clic, onglet reste ouvert) : ce
      // n'est pas un echec, l'etape est franchie. La bloquer ferait croire
      // a une panne.
      if (err.kind === "conflict") {
        onDone();
        return;
      }
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="stack">
      <section className="card">
        <h1 className="card__title">{view.title}</h1>
        <p className="card__lead">{L.readBeforeSigning}</p>

        {level && <p className="level">{level}</p>}

        {/* Le message de l'emetteur, tel qu'il l'a ecrit. `white-space:
            pre-wrap` sur .issuer-message preserve ses retours a la ligne ;
            sujet et corps sont deux blocs distincts, un "\n" ecrit dans du
            JSX etant une barre oblique suivie d'un n, pas un saut de
            ligne. */}
        {(view.message_subject || view.message_body) && (
          <div className="issuer-message">
            {view.message_subject && (
              <p style={{ margin: "0 0 0.5rem", fontWeight: 600 }}>{view.message_subject}</p>
            )}
            {view.message_body && <p style={{ margin: 0 }}>{view.message_body}</p>}
          </div>
        )}

        <p className="muted">
          {view.signer_count && view.signer_count > 1 && view.signer_order
            ? L.signerRank(view.signer_order, view.signer_count) + " · "
            : ""}
          {view.expires_at
            ? `${L.expiresOn} ${new Date(view.expires_at).toLocaleDateString(undefined, {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}`
            : ""}
        </p>
      </section>

      <section className="card stack">
        {viewerFailed ? (
          <>
            {/* La visionneuse n'a rien pu afficher : on ne peut pas exiger
                un defilement qui n'aura pas lieu. Le signataire telecharge
                et declare - et la preuve enregistre que c'est ce qui s'est
                passe, pas un affichage page par page. */}
            <p>{L.viewerFailed}</p>
            <div className="actions">
              <button type="button" className="btn btn--secondary" onClick={onDownload}>
                {L.download}
              </button>
            </div>
            <label className="consent">
              <input
                type="checkbox"
                checked={declare}
                onChange={(e) => {
                  setDeclare(e.target.checked);
                  setTouched(true);
                }}
              />
              <span>{L.declareRead}</span>
            </label>
            {touched && !declare && (
              <p className="alert alert--error" role="alert">
                {L.declareRequired}
              </p>
            )}
          </>
        ) : (
          <>
            <p className="lecture__compteur" aria-live="polite">
              {pageCount > 0 ? L.pagesSeen(vues, pageCount) : L.loading}
            </p>
            {/* La barre double le compteur pour ceux qui lisent une forme
                plutot qu'un chiffre. Purement decorative : le compteur
                au-dessus porte deja l'information pour les lecteurs
                d'ecran. */}
            <div className="jauge" aria-hidden="true">
              <div
                className="jauge__remplie"
                style={{ "--part": pageCount > 0 ? vues / pageCount : 0 } as CSSProperties}
              />
            </div>
            <p className="muted">{finAtteinte ? L.allPagesSeen : L.readAllPages}</p>
            {/* Sans ce bouton, un signataire devant un contrat de quarante
                pages n'a que son doigt pour atteindre le bas, et le bouton
                « Continuer » reste inactif sans qu'il sache pourquoi. Il ne
                dispense de rien : le compteur dira ce qu'il a affiche. */}
            {!finAtteinte && pageCount > 0 && (
              <div className="actions">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => onAllerALaPage(premiereNonVue ?? pageCount)}
                >
                  {L.afficherLaPage(premiereNonVue ?? pageCount)}
                </button>
              </div>
            )}
          </>
        )}

        {error && <ErrorPanel error={error} L={L} />}

        {/* Le refus est visible DES cet ecran, jamais relegue en bas de
            page : un signataire qui ne veut pas signer doit pouvoir le
            dire sans avoir a franchir des etapes - et sans avoir a tout
            lire d'abord. */}
        <div className="actions actions--split">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void submit()}
            disabled={!documentPret || !pretAContinuer || submitting}
          >
            {L.continueToConsent}
          </button>
          <button type="button" className="btn btn--danger" onClick={onDecline}>
            {L.refuse}
          </button>
        </div>
      </section>
    </div>
  );
}
